import React, { useEffect, useRef, useState } from 'react';
import { Modal, TextInput } from '@mantine/core';
import type { TenbisChargeRequest } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import { noSelectionTextInputProps } from '../../receipt/receipt-input.utils';
import classes from './tenbis-card-dialog.module.scss';

export type TenbisCardSubmit = { code: string; operationType: TenbisChargeRequest['operationType']; transactionId?: string };

type TenbisCardDialogProps = {
  isOpen: boolean;
  mode: 'charge' | 'refund';
  amountLabel: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: TenbisCardSubmit) => void;
};

// Wedge track2 (16+ digits, often with '=' / ';') => first 16 digits as a swipe; shorter = typed.
export function parseTenbisCode(raw: string): { code: string; operationType: 'auto' | 'manual' } | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 4) {
    return null;
  }
  const isSwipe = digits.length >= 16 || /[=;]/.test(raw);
  return { code: isSwipe ? digits.slice(0, 16) : digits.slice(0, 19), operationType: isSwipe ? 'auto' : 'manual' };
}

export const TenbisCardDialog: React.FC<TenbisCardDialogProps> = ({
  isOpen,
  mode,
  amountLabel,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const [raw, setRaw] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const submitLockRef = useRef(false);

  useEffect(() => {
    setRaw('');
    setTransactionId('');
    submitLockRef.current = false;
  }, [isOpen]);

  const parsed = parseTenbisCode(raw);
  const isRefund = mode === 'refund';
  const canSubmit = !isSubmitting && (parsed !== null || (isRefund && transactionId.trim().length > 0));

  const handleSubmit = () => {
    if (!canSubmit || submitLockRef.current) {
      return;
    }
    submitLockRef.current = true;
    onSubmit({
      code: parsed?.code ?? '',
      operationType: parsed?.operationType ?? 'manual',
      transactionId: isRefund && transactionId.trim() ? transactionId.trim() : undefined,
    });
  };

  useEffect(() => {
    if (!isSubmitting) {
      submitLockRef.current = false;
    }
  }, [isSubmitting]);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      centered
      dir="rtl"
      radius="lg"
      size={420}
      title={isRefund ? 'זיכוי תן ביס' : 'תשלום תן ביס'}
      closeButtonProps={{ 'data-testid': 'tenbis-card-close' } as Record<string, string>}
    >
      <div className={classes.body} data-testid="tenbis-card-dialog">
        <p className={classes.hint}>{isRefund ? 'העבר את הכרטיס או הקלד מספר עסקה מהקבלה' : 'העבר את הכרטיס או הקלד את קוד האפליקציה'}</p>
        <TextInput
          label={isRefund ? 'קוד כרטיס (לזיכוי ללא מספר עסקה)' : 'קוד כרטיס / אפליקציה'}
          data-testid="tenbis-card-code"
          data-autofocus
          autoComplete="off"
          inputMode="numeric"
          {...noSelectionTextInputProps}
          value={raw}
          onChange={(event) => setRaw(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleSubmit();
            }
          }}
        />
        {isRefund && (
          <TextInput
            label="מספר עסקה תן ביס (לא חובה)"
            data-testid="tenbis-card-transaction-id"
            autoComplete="off"
            inputMode="numeric"
            {...noSelectionTextInputProps}
            value={transactionId}
            onChange={(event) => setTransactionId(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSubmit();
              }
            }}
          />
        )}
        <div className={classes.amountRow}>
          <span className={classes.amountLabel}>{isRefund ? 'סכום לזיכוי' : 'סכום לחיוב'}</span>
          <span dir="ltr">{amountLabel}</span>
        </div>
        <div className={classes.buttonRow}>
          <Button variant="cancel" onClick={onClose} disabled={isSubmitting} dataTestId="tenbis-card-cancel">
            ביטול
          </Button>
          <Button
            variant="confirm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
            loadingLabel="מחייב..."
            dataTestId="tenbis-card-submit"
          >
            אישור
          </Button>
        </div>
      </div>
    </Modal>
  );
};
